import { For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { SkipLine } from '../../lib/card-controls';
import { useCardControls } from '../../lib/card-controls-context';
import { StopConfirm } from './StopConfirm';

// What shows under one job or epic after a control (#6753): the Stop confirm, the Stop note
// with Undo (prototype stopNoteHTML `:2154`, 6 500 ms) and then the answer, the translated
// refusal (never the server's `detail`), and on an epic the slices its pause or resume
// skipped, each with its reason. A refusal a gone, offline or unreadable machine gave a
// resume carries "Resume on any machine", which sends the single-job resume with
// `{anywhere: true}` (deviation 6).

export function ControlNotices(props: {
  readonly kind: 'job' | 'epic';
  readonly id: string;
  /** "Coder" for a job, the epic's title for an epic. */
  readonly name: string;
  readonly onConfirm: () => void;
  readonly skips?: readonly SkipLine[];
}): JSX.Element {
  const handle = useCardControls();
  const on = () => (handle?.on() === true ? handle : null);
  return (
    <Show when={on()}>
      {(h) => {
        const card = () => h().card;
        const mine = <T extends { readonly kind: string; readonly id: string }>(x: T | null) =>
          x !== null && x.kind === props.kind && x.id === props.id ? x : null;
        const job = props.kind === 'job';
        return (
          <>
            <Show when={mine(card().ask())}>
              <StopConfirm
                id={props.id}
                label={t(job ? 'fleet.machines.ctl_job_stop' : 'fleet.machines.ctl_epic_stop', {
                  name: props.name,
                  title: props.name,
                })}
                text={t(
                  job ? 'fleet.machines.ctl_job_stop_ask' : 'fleet.machines.ctl_epic_stop_ask',
                  { name: props.name, title: props.name },
                )}
                yes={t(
                  job ? 'fleet.machines.ctl_job_stop_yes' : 'fleet.machines.ctl_epic_stop_yes',
                )}
                no={t(job ? 'fleet.machines.ctl_job_stop_no' : 'fleet.machines.ctl_epic_stop_no')}
                onYes={props.onConfirm}
                onNo={() => card().cancelAsk()}
              />
            </Show>
            <Show when={card().noteFor(props.kind, props.id)}>
              {(n) => (
                <div class="mission-ctl__note">
                  {/* Not live: the card's live region says it once (`card.live()`). */}
                  <span>{n().text}</span>
                  <Show when={n().undo}>
                    <button
                      type="button"
                      class="mission-ctl__link"
                      data-undo-for={props.id}
                      onClick={() => card().undo(props.kind, props.id)}
                    >
                      {t('fleet.machines.ctl_undo')}
                    </button>
                  </Show>
                </div>
              )}
            </Show>
            <Show when={card().refusal(props.id)}>
              {(r) => (
                <p class="mission-ctl__refusal" role="status">
                  <span>{r().text}</span>
                  <Show when={job && r().anywhere}>
                    <button
                      type="button"
                      class="mission-ctl__link"
                      aria-disabled={card().locked() > 0 ? 'true' : undefined}
                      onClick={() => {
                        if (card().locked() === 0) card().resumeAnywhere(props.id);
                      }}
                    >
                      {t('fleet.machines.ctl_resume_anywhere')}
                    </button>
                  </Show>
                </p>
              )}
            </Show>
            <Show when={(props.skips?.length ?? 0) > 0}>
              <ul class="mission-ctl__skips">
                <For each={props.skips}>
                  {(s) => (
                    <li>
                      <span>
                        {t('fleet.machines.ctl_skipped_line', { name: s.name, reason: s.text })}
                      </span>
                      <Show when={s.anywhere}>
                        <button
                          type="button"
                          class="mission-ctl__link"
                          aria-disabled={card().locked() > 0 ? 'true' : undefined}
                          onClick={() => {
                            if (card().locked() === 0) card().resumeAnywhere(s.taskId);
                          }}
                        >
                          {t('fleet.machines.ctl_resume_anywhere')}
                        </button>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </>
        );
      }}
    </Show>
  );
}
